import React, { useEffect, useRef, useState } from 'react';
import { I18n } from 'react-redux-i18n';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from 'semantic-ui-react';

import * as stripeActions from './reducer';
import { Record } from '../../modules/bin/utility';
import { RootState } from '../../store';
import { RouteParams } from '../../types/routeParams';

const StripeConnector = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { operation } = useParams<RouteParams>();
  const [message, setMessage] = useState(I18n.t('stripe.connection-message'));
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const inFlightRef = useRef<boolean>(false);

  const stripeConnectRecord = useSelector(
    (state: RootState) => state.stripe?.stripeConnectRecord
  );

  const setMessageTimeout = (path: string, url: string, timeout = 10000) => {
    setMessage(I18n.t(path));
    setTimeout(() => navigate(url), timeout);
  };

  useEffect(() => {
    if (operation === 'connect' || operation === 'onboard') {
      connectWithStripe();
    } else if (operation === 'error') {
      setMessageTimeout('stripe.error-message', '/');
    } else {
      navigate('/');
    }
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!Record.isRecordReady(stripeConnectRecord)) return;

    // expected response shape now: { data: { organizationId, status } }
    const payload = stripeConnectRecord?.data;
    // defensive guards
    const organizationId = payload?.data?.organizationId ?? payload?.organizationId ?? null;
    const status = (payload?.data?.status ?? payload?.status)?.toLowerCase?.();

    if (!status) return; // nothing to do yet

    if (status === 'completed') {
      // success path
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
      inFlightRef.current = false;
      if (!organizationId) {
        // treat missing org id as error
        setMessageTimeout('stripe.error-message', '/');
        return;
      }
      // Show success then hard reload to target URL (full page refresh)
      setMessage(I18n.t('stripe.success-message'));
      setTimeout(() => {
        window.location.assign(`/organizations/edit/${organizationId}/finance`);
      }, 3500);
      return;
    }

    if (status === 'inprogress') {
      // start or continue polling
      if (startTimeRef.current === null) {
        setMessage(I18n.t('stripe.processing-message'));
        startTimeRef.current = Date.now();
      }
      scheduleNextPoll();
    } else {
      // unknown status -> error fallback
      setMessageTimeout('stripe.error-message', '/');
    }
  }, [stripeConnectRecord]);

  const scheduleNextPoll = () => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
    }
    // timeout after 3 minutes
    const elapsed = startTimeRef.current ? Date.now() - startTimeRef.current : 0;
    const timeoutLimitMs = 3 * 60 * 1000; // 3 minutes
  if (elapsed >= timeoutLimitMs) {
      setMessageTimeout('stripe.error-message', '/');
      return;
    }
    pollingRef.current = setTimeout(() => {
      if (!inFlightRef.current) {
        // reuse same request payload (state/scope/code) for idempotent POST
    connectWithStripe();
      }
    }, 5000); // 5s interval
  };

  const connectWithStripe = () => {
    const { stripeConnect } = stripeActions;
    const searchParams = new URLSearchParams(location.search);
    const state = searchParams.get('state');
    const code = searchParams.get('code');
    const scope = searchParams.get('scope');

    // // old method where all 3 objects are available
    // if (!state || !code || !scope) {
    //   history.push('/');
    //   return;
    // }
    // // continue if only state is available

    // Simple throttle: check if we should wait before making this call
    const throttleKey = `stripe_throttle_${state}`;
    const throttleUntil = sessionStorage.getItem(throttleKey);
    const now = Date.now();
    
    if (throttleUntil && now < parseInt(throttleUntil)) {
      // Too soon, skip this call
      return;
    }

    // Set next allowed call time (5 seconds from now)
    sessionStorage.setItem(throttleKey, String(now + 5000));

    if (inFlightRef.current) return; // prevent overlapping
    inFlightRef.current = true;
    const action: any = stripeConnect(state, scope, code);
    // We rely on RSAA middleware; to know when it's done we can wrap dispatch promise (redux-api-middleware returns a promise)
    const result = dispatch(action) as Promise<any>;
    if (result && typeof (result as any).then === 'function') {
      (result as any).finally(() => {
        inFlightRef.current = false;
      });
    } else {
      // fallback: release lock after a delay to avoid deadlock
      setTimeout(() => {
        inFlightRef.current = false;
      }, 1500);
    }
  };

  return (
    <div>
      <Loader active content={message} />
    </div>
  );
};

export default StripeConnector;
